/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import type { Unsubscribe } from '@amazon/vinyl-util'
import { documentEvents } from '@/util/interaction'

/*
 * Shared visibility gate for chat subscriptions.
 *
 * While the tab is backgrounded, chat subscriptions shouldn't churn the
 * DOM. The observables are module singletons shared across the app
 * (session persistence, telemetry, audio playback all still want to
 * keep processing) — so instead of intercepting the observable, we
 * wrap the SUBSCRIBER-side handler.
 *
 * Semantics:
 *   - Visible on mount + emission arrives → handler fires immediately.
 *   - Hidden on emission arrival → handler DOES NOT fire; the latest
 *     value is buffered.
 *   - `visibilitychange` transitions hidden → visible → handler fires
 *     exactly once with the most-recent buffered value.
 *   - Multiple emissions while hidden collapse into a single trailing
 *     invocation on resume — rendering is idempotent so the tail
 *     state is what the user needs.
 *   - Handler returns a disposer that removes the visibilitychange
 *     listener.
 */

/**
 * Sentinel used to distinguish "no value has arrived yet" from
 * `undefined` being a legitimate value the caller could pass. Never
 * exported; strict identity check makes it collision-proof with any
 * caller-provided payload.
 */
const NOT_SET: unique symbol = Symbol('NOT_SET')

/**
 * True when the document is currently visible (or the surrounding env
 * has no notion of visibility — treated as visible).
 */
function isDocumentVisible(): boolean {
    if (typeof document === 'undefined') return true
    try {
        return document.visibilityState === 'visible'
    } catch {
        // Very old UAs / test shims that throw on the getter — assume
        // visible so we don't lock the DOM into a permanently-buffered
        // state.
        return true
    }
}

/**
 * Wrap `handler` so it only fires while the document is visible.
 *
 * When hidden, the latest value is buffered and the handler is invoked
 * exactly once with that value on the next `visibilitychange` →
 * `visible` transition. Multiple buffered emissions collapse into a
 * single trailing invocation (the tail wins — rendering is idempotent
 * so we don't replay history).
 *
 * Returns a disposer that removes the internal `visibilitychange`
 * listener. Call this alongside the observable's own unsubscribe on
 * component teardown.
 */
export function gateHandlerOnVisibility<T>(handler: (value: T) => void): {
    /** Wrapped handler — pass this to `observable.onData(...)`. */
    readonly onEmit: (value: T) => void
    /** Disposer — removes the visibilitychange listener. */
    readonly dispose: () => void
} {
    // `NOT_SET` means "nothing new arrived since the last flush" so we
    // don't re-fire the handler on every stray visibility ping.
    let buffered: T | typeof NOT_SET = NOT_SET

    function flush(): void {
        if (buffered === NOT_SET) return
        // Snapshot then clear BEFORE invoking the handler so a
        // re-entrant emission inside the handler buffers fresh instead
        // of being lost to a stale local.
        const value = buffered
        buffered = NOT_SET
        handler(value)
    }

    function onVisibilityChange(): void {
        if (isDocumentVisible()) {
            flush()
        }
    }

    const hasDocument = typeof document !== 'undefined'
    let unsubVisibility: Unsubscribe | undefined
    if (hasDocument) {
        unsubVisibility = documentEvents.on('visibilitychange', onVisibilityChange)
    }

    function onEmit(value: T): void {
        if (isDocumentVisible()) {
            // Clear any stale buffered value defensively so a
            // visibility flip that races with a visible emission
            // doesn't double-fire.
            buffered = NOT_SET
            handler(value)
            return
        }
        // Older buffered emissions are overwritten; rendering is
        // idempotent so the tail is what we need on resume.
        buffered = value
    }

    function dispose(): void {
        unsubVisibility?.()
        buffered = NOT_SET
    }

    return { onEmit, dispose }
}

/**
 * Fires `handler` on every hidden → visible transition. Unlike
 * `gateHandlerOnVisibility`, there is no buffered value to replay — this is
 * a bare "the tab just became visible" signal for callers with no
 * observable to gate (e.g. resetting an inactivity timer).
 */
export function onBecameVisible(handler: () => void): Unsubscribe {
    if (typeof document === 'undefined') return () => {}
    return documentEvents.on('visibilitychange', () => {
        if (isDocumentVisible()) handler()
    })
}
